/* eslint-disable camelcase */
import isEmpty from 'lodash/isEmpty';
import find from 'lodash/find';
import xor from 'lodash/xor';
import { uniqBy } from 'lodash';
import compact from 'lodash/compact';
import { baseApi, parameterize } from './base.service';
import { Params, PATH } from './__fixtures__/service-fixtures';
import {
  getProjectID,
  newProjectSchema,
  editProjectSchema,
  Project,
  projectSchema,
} from '../projects/projects.feature';
import { setDefaults } from '../permissions/permissions.slice';
import { MemberPermissions } from '../permissions/members-permissions';
import changePermissions from './fetch/change_permissions';
import redirectTo from '../utils/redirectTo';
import Membership from '../models/membership';
import Group from '../models/groups';
import keysToCamelCase from '../utils/keys-to-camel-case';
import { allRoles } from './roles.service';
import { Role } from '../models/role';
import { memberIdsFromGroups } from '../permissions/selectors/get-list-items';
import { RootState } from '../store';

type FormSchema = ReturnType<
  typeof editProjectSchema | typeof newProjectSchema
>;
export interface ResultType extends ProjectsRes, PermissionRoles {
  initialFormFields: FormSchema;
  memberships: Membership[];
  groups: Group[];
}

interface GetResponse {
  projects: Project[];
  totalPages: number;
  totalCount: number;
}

export interface PermissionRoles {
  user: {
    roles: Role[];
    owners: string[];
  };
}

interface ProjectsRes {
  project: Partial<Project>;
}

type ProjectViewRequest = Partial<{ project: Project, initialProject: Project, currentMember: Membership }>
& MemberPermissions;

export const projectsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getProjects: build.query<GetResponse, Partial<Params>>({
      query: (params) => `/api/v2/projects.json?${parameterize(params)}`,
      transformResponse: (response: { projects: Record<string, any>, total_pages: number, total_count: number }) => {
        return {
          projects: Object.values(response.projects),
          totalPages: response.total_pages,
          totalCount: response.total_count,
        };
      },
      providesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
    fetchProjectsView: build.query<ResultType, void>({
      async queryFn(_arg, api, _extraOptions, baseQuery) {
        let initialFormFields: FormSchema;
        let project: Partial<Project> = {};
        let currentMember: Membership | null = null;

        const id = getProjectID();
        const currentUserID = (api.getState() as RootState).app.user.id;
        const { data: membershipData }: any = await baseQuery(PATH.memberships);
        const { data: groupsData }: any = await baseQuery(`${PATH.groups}?associations=true`);
        const { data: rolesData }: any = await baseQuery(`${PATH.roles}?all=true&sort=name`);
        const { memberships } = membershipData;

        const roles = rolesData.roles.map((role: Role) => ({
          name: role.name,
          id: role.name.toLowerCase(),
        }));

        const owners = rolesData.roles
          .filter((role: Role) => role.can_manage_roles)
          .map((owner: Role) => owner.name);

        roles.unshift(allRoles);

        [currentMember] = memberships.filter((member) => member.user_id === currentUserID);

        if (id) {
          const projectResponse: any = await baseQuery(`${PATH.projects}/${id}.json?associations=true`);
          if (projectResponse.error) {
            return projectResponse;
          }

          const { data: { project: projectData } } = projectResponse;
          projectSchema.parse(projectData); // runtime type validation
          initialFormFields = editProjectSchema(memberships, projectData);
          project = projectData;
        } else {
          initialFormFields = newProjectSchema(memberships);
        }

        return {
          data: {
            project,
            initialFormFields,
            memberships: keysToCamelCase(membershipData.memberships),
            groups: keysToCamelCase(groupsData.groups),
            user: { roles, owners, currentMember },
          },
        };
      },
      async onQueryStarted(_args, { queryFulfilled, dispatch }) {
        let res;
        try {
          res = await queryFulfilled;
        } catch (e) {
          console.error('Query failed', e);
          return;
        }
        const { user, groups } = res.data;
        let defaultGroups: Group[] = [];
        let defaultMembers: Membership[] = [];

        if (isEmpty(res.data.memberships) && isEmpty(res.data.groups)) {
          return;
        }

        if (!isEmpty(res.data.groups)) {
          const { project } = res.data;
          const { group_ids = [] } = project;

          defaultGroups = group_ids.map((id) => find(groups, { id })) as Group[];
          // some of the project groups might not be in the groups list cuz of the current user permission. the previous line makes those groups undefined so we need to compact them
          defaultGroups = compact(defaultGroups);
        }

        if (!isEmpty(res.data.memberships)) {
          const { memberships, project } = res.data;
          const { member_ids = [] } = project;
          let selected: Membership[] = [];

          if (project.id) {
            selected = member_ids.map((id: string) => find(memberships, { id }));
          } else {
            selected = [user.currentMember];
          }
          selected = compact(selected);

          const managed = memberships.filter(
            (member) => user.owners?.includes(member.roleName) // isOwner
            || memberIdsFromGroups(defaultGroups).includes(member.id), // isGroupManaged
          );

          defaultMembers = uniqBy([...managed, ...selected], 'id');
        }

        dispatch(setDefaults({ members: defaultMembers, groups: defaultGroups, user }));
      },
    }),
    mutateProjectsView: build.mutation<{}, ProjectViewRequest>({
      async queryFn({
        project, memberIDs, groupIDs, totalSelected, initialProject, currentMember,
      },
      _api,
      _extraOptions,
      baseQuery) {
        let projectID = '';
        let membershipsPermissionsRes = {};
        let groupsPermissionsRes = {};

        if (!currentMember) {
          throw new Error('Member is required');
        }

        const res = project?.id
          ? await baseQuery({
            url: `${PATH.projects}/${project.id}.json`,
            method: 'PUT',
            body: project,
          })
          : await baseQuery({
            url: PATH.projects,
            method: 'POST',
            body: project,
          });

        if (res.error) {
          return { error: res.error };
        }

        projectID = (res.data as ProjectsRes).project.id as string;

        /**
         * NOTE : ~ Initial State Comparison ~
         * This check ensures that we only make the call when there is
         * a change in selections - with comparisons to the initial Project state.
         * If/When we convert the list view for Projects, we will need
         * to invalidate the tags in order to receive new data for this comparison
         */
        if (!isEmpty(xor(initialProject?.group_ids, groupIDs))) {
          const addAll = totalSelected?.hasTotalGroupsSelected;
          groupsPermissionsRes = await changePermissions(baseQuery, {
            path: PATH.projects,
            body: {
              type: 'project_groups',
              project_id: projectID,
              set: addAll ? [] : groupIDs,
              add_all: addAll,
            },
          });
        }

        const creatorRemoved = !project?.id && !memberIDs.includes(currentMember.id);
        if (!isEmpty(xor(initialProject?.member_ids, memberIDs)) || creatorRemoved) {
          const addAll = totalSelected?.hasTotalMembersSelected;
          let set = creatorRemoved
            ? [...memberIDs, currentMember.id]
            : memberIDs;
          if (addAll) {
            set = [];
          }
          membershipsPermissionsRes = await changePermissions(baseQuery, {
            path: PATH.projects,
            body: {
              type: 'project_members',
              project_id: projectID,
              set,
              add_all: addAll,
            },
          });
        }

        redirectTo.projects();

        return {
          data: {
            res,
            membership_permissions: membershipsPermissionsRes,
            group_permissions: groupsPermissionsRes,
          },
        };
      },
    }),
    deleteProject: build.mutation<void, string>({
      query: (id) => ({
        url: `/api/v2/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'Projects', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetProjectsQuery,
  useFetchProjectsViewQuery,
  useMutateProjectsViewMutation,
  useDeleteProjectMutation,
} = projectsApi;
